/**
 * WS How It Works — Circular layer-reveal that follows the pointer.
 * Matches reference: circle(110px at var(--cx) var(--cy))
 * - pointerenter  → add is-hovered, set position, expand circle with ease
 * - pointermove   → update --cx/--cy instantly (no lerp), move cursor el
 * - pointerleave  → collapse circle with ease, remove is-hovered
 * - click (touch) → toggle on touch devices
 */
(() => {
  const RADIUS_PX   = 110;   // matches reference circle(110px ...)
  const ENTER_MS    = 350;   // ease-in duration
  const LEAVE_MS    = 300;   // ease-out duration

  function init(visual) {
    const innerImg = visual.querySelector('.ws-hiw-img--inner');
    const cursorEl = visual.querySelector('.ws-hiw-cursor');
    if (!innerImg) return;

    // ── helpers ──────────────────────────────────────────────────────────────

    function setPos(x, y) {
      visual.style.setProperty('--cx', x + 'px');
      visual.style.setProperty('--cy', y + 'px');
      if (cursorEl) {
        cursorEl.style.left = x + 'px';
        cursorEl.style.top  = y + 'px';
      }
    }

    function setRadius(r, instant) {
      // instant = no transition (during move), eased = enter/leave
      if (instant) {
        innerImg.style.transition = 'none';
        innerImg.style.webkitTransition = 'none';
      } else if (r > 0) {
        innerImg.style.transition = `clip-path ${ENTER_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`;
        innerImg.style.webkitTransition = `-webkit-clip-path ${ENTER_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`;
      } else {
        innerImg.style.transition = `clip-path ${LEAVE_MS}ms ease`;
        innerImg.style.webkitTransition = `-webkit-clip-path ${LEAVE_MS}ms ease`;
      }
      const clip = `circle(${r}px at var(--cx, 50%) var(--cy, 50%))`;
      innerImg.style.clipPath = clip;
      innerImg.style.webkitClipPath = clip;
    }

    // ── enter ────────────────────────────────────────────────────────────────

    function onEnter(e) {
      const rect = visual.getBoundingClientRect();
      const x = (e.clientX ?? e.touches?.[0]?.clientX ?? rect.width  / 2) - rect.left;
      const y = (e.clientY ?? e.touches?.[0]?.clientY ?? rect.height / 2) - rect.top;
      setPos(x, y);
      visual.classList.add('is-hovered');
      // expand with ease
      setRadius(RADIUS_PX, false);
    }

    // ── move ─────────────────────────────────────────────────────────────────

    function onMove(e) {
      if (!visual.classList.contains('is-hovered')) return;
      const rect = visual.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      // Update position instantly — no transition during move
      setPos(x, y);
      // Keep circle at full radius with no transition (tracks cursor)
      setRadius(RADIUS_PX, true);
    }

    // ── leave ────────────────────────────────────────────────────────────────

    function onLeave() {
      visual.classList.remove('is-hovered');
      // collapse with ease
      setRadius(0, false);
    }

    // ── touch toggle ─────────────────────────────────────────────────────────

    function isMobile() {
      return window.matchMedia('(max-width: 768px)').matches;
    }

    function onTap() {
      if (!isMobile()) return;
      if (visual.classList.contains('is-tapped')) {
        visual.classList.remove('is-tapped');
        // Clear inline style so CSS transition back to circle(0px) works
        innerImg.style.clipPath = '';
        innerImg.style.webkitClipPath = '';
        innerImg.style.transition = `clip-path ${LEAVE_MS}ms ease`;
        innerImg.style.webkitTransition = `-webkit-clip-path ${LEAVE_MS}ms ease`;
      } else {
        visual.classList.add('is-tapped');
        // Full-reveal clip-path driven by CSS — clear any JS inline override
        innerImg.style.clipPath = '';
        innerImg.style.webkitClipPath = '';
      }
    }

    // ── bind ─────────────────────────────────────────────────────────────────

    visual.addEventListener('pointerenter', function(e) { if (!isMobile()) onEnter(e); });
    visual.addEventListener('pointermove',  function(e) { if (!isMobile()) onMove(e); });
    visual.addEventListener('pointerleave', function(e) { if (!isMobile()) onLeave(e); });
    visual.addEventListener('click',        onTap);

    const tapBtn = visual.querySelector('.ws-hiw-tap-btn');
    if (tapBtn) {
      tapBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        onTap();
      });
    }

    // Initial state
    setRadius(0, true);
    setPos(50, 50);
  }

  function boot() {
    document.querySelectorAll('.ws-hiw-visual').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
